/**
 * SignaturePage - Digital signature for commitment
 */
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Loader2, CheckCircle } from 'lucide-react';
import { toast } from 'sonner';
import Header from '../components/shared/Header';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SignaturePage = () => {
  const navigate = useNavigate();
  const [signature, setSignature] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSign = async () => {
    if (!signature.trim()) {
      toast.error('Please type your name to sign');
      return;
    }

    setLoading(true);
    try {
      const relationshipId = sessionStorage.getItem('relationship_id');
      const userId = sessionStorage.getItem('user_id');

      const response = await axios.post(`${API}/signatures?user_id=${userId}`, {
        relationship_id: relationshipId,
        typed_signature: signature
      });

      toast.success('Signature recorded successfully!');
      
      // Check if both signed
      if (response.data.both_signed) {
        // Both signed - go to reveal
        const revealToken = response.data.reveal_token;
        if (revealToken) {
          navigate(`/reveal/${relationshipId}?token=${revealToken}`);
        } else {
          navigate(`/reveal/${relationshipId}`);
        }
      } else {
        // Waiting for partner
        navigate('/waiting');
      }
    } catch (error) {
      console.error('Error saving signature:', error);
      toast.error('Failed to save signature');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <div className="flex items-center justify-center p-6" style={{minHeight: 'calc(100vh - 100px)'}}>
        <div className="max-w-lg w-full bg-white rounded-2xl border border-stone-100 shadow-sm p-12">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-bold text-stone-900 mb-4">Sign Your MarrsingClause®</h2>
            <p className="text-stone-600">
              By typing your name, you affirm your conscious commitment to this MarrsingClause®.
            </p>
          </div>

          <div className="mb-8">
            <label className="block text-sm font-medium text-stone-700 mb-2">
              Type Your Full Name *
            </label>
            <input
              type="text"
              value={signature}
              onChange={(e) => setSignature(e.target.value)}
              className="w-full h-16 rounded-lg border-stone-200 bg-stone-50 px-4 text-2xl focus:border-stone-900 focus:ring-0 transition-all font-cormorant italic"
              placeholder="Your Name"
              data-testid="signature-input"
            />
          </div>

          <div className="bg-stone-50 rounded-xl p-6 mb-8">
            <p className="text-sm text-stone-600">
              This signature represents your voluntary participation and conscious commitment. 
              It does not create a legal contract.
            </p>
          </div>

          <button
            onClick={handleSign}
            disabled={loading || !signature.trim()}
            className="w-full bg-mc-blue-deep text-white hover:bg-mc-blue-dark rounded-full px-8 py-4 text-lg font-medium transition-all hover:scale-[1.02] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            data-testid="submit-signature"
          >
            {loading ? (
              <>
                <Loader2 className="w-5 h-5 animate-spin" />
                Signing...
              </>
            ) : (
              <>
                Sign & Finalize
                <CheckCircle className="w-5 h-5" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default SignaturePage;
